// Housekeeping (ama de llaves / supervisora). Hotel → Housekeeping.
// 948 — control de camareras, fase 1: tablero en vivo por piso (quién está en
// qué habitación y desde cuándo, atrasos, terminadas muy rápido), planificación
// del día con reparto propuesto por créditos y pisos, asignación con tope de
// créditos (superarlo exige justificación), prioridad pedida por recepción y
// catálogo de camareras (PIN, vínculo opcional a Nómina). La camarera trabaja
// en su celular desde «Mi día» (/hotel/mi-dia).
// 949 — calidad: inspección con defectos y puntaje (re-limpieza bajo el
// umbral), llaves maestras, cierre de turno y ajustes (HousekeepingCalidad.tsx).
// 950 — gestión: indicadores, horas fuera de jornada → Nómina, bono y QR de
// puertas (HousekeepingGestion.tsx).
// 951 — profundas por rotación, amenities/lencería por piso (bodega) y rondas
// de áreas públicas (HousekeepingInsumos.tsx).
// 937: «no molestar», minibar al folio, objetos perdidos y discrepancias.
// hotel.habitacion_limpieza exige nivel ≥3 para «inspeccionada»; asignar,
// planificar y administrar camareras, nivel ≥3 (lo valida el servidor).
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, BellOff, ClipboardCheck, ClipboardList, Clock, DoorClosed, DoorOpen, LogIn, LogOut, Package, Smartphone, Sparkles, SprayCan, Star, Users, Wine, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { useTabsStore } from '@/stores/tabs.store';
import {
  Campo, Modal, Panel, fieldCls, money, fmtFecha, hotelRpc, LIMPIEZA, MANT, QK, HkHabitacion, CamareraHk,
  ESTADO_TAREA_HK, MOTIVO_PAUSA, etiquetaPiso, useDialogoEnLinea,
} from './shared';
import { AjustesHk, CierreTurno, Inspeccion, Llaves, PanelInspeccion } from './HousekeepingCalidad';
import { GestionHk } from './HousekeepingGestion';
import { AjustesF4, AreasEstadoBanda, Insumos, Profundas } from './HousekeepingInsumos';

type Filtro = 'sucias' | 'todas' | 'sin_asignar' | 'en_curso';
type Vista = 'tablero' | 'inspeccion' | 'profundas' | 'insumos' | 'camareras' | 'llaves' | 'gestion' | 'objetos' | 'discrepancias' | 'ajustes';
interface Objeto { id: string; codigo: string; fecha: string; habitacion: string | null; lugar: string | null; descripcion: string; encontrado_por: string | null; estado: string; huesped: string | null; reserva: string | null }
interface Discrepancia { id: string; fecha: string; habitacion: string; observado: string; sistema: string; reserva: string | null; nota: string | null }
interface ProdMinibar { id: string; codigo: string; nombre: string; precio: number }
interface Propuesta { camarera_id: string; nombre: string; habitaciones: { id: string; numero: string; piso: string | null; creditos: number }[]; carga: number; tope: number; excede: boolean }

const natural = (a: string, b: string) => a.localeCompare(b, 'es', { numeric: true });
const atrasada = (h: HkHabitacion) => h.tarea_estado === 'en_proceso' && h.minutos != null && h.estandar != null && h.minutos > h.estandar * 1.5;

type PedirJustificacion = (detalle: string) => Promise<string | null>;
// Asigna; si el servidor rechaza por tope de créditos, pide la justificación (en
// la pantalla, no con una ventana) y reintenta.
async function asignarConTope(habitaciones: string[], camarera: string | null, pedir: PedirJustificacion, justificacion?: string): Promise<boolean> {
  try {
    await hotelRpc('hk_asignar_tareas', { _habitaciones: habitaciones, _camarera: camarera, _justificacion: justificacion ?? null });
    return true;
  } catch (e) {
    const msg = getErrorMessage(e);
    if (!msg.startsWith('TOPE:')) { toast.error(msg); return false; }
    const j = await pedir(msg.replace(/^TOPE:\s*/, ''));
    if (!j) return false;
    return asignarConTope(habitaciones, camarera, pedir, j);
  }
}

export function HousekeepingHotel() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const pedirJust: PedirJustificacion = async (detalle) =>
    (await dlg.pedir('Supera el tope de créditos', [{ key: 'j', label: 'Justificación (queda registrada)', minimo: 5, multilinea: true }], { detalle, aceptar: 'Asignar igual' }))?.j.trim() ?? null;
  const { hasAccessLevel } = usePermissions();
  const openTab = useTabsStore((s) => s.openTab);
  const esSupervisora = hasAccessLevel(3);
  const { data: habs = [], isLoading, isFetching } = useQuery<HkHabitacion[]>({
    queryKey: [QK, 'housekeeping'], queryFn: () => hotelRpc<HkHabitacion[]>('housekeeping', {}),
    refetchInterval: 15000,
  });
  const { data: camareras = [] } = useQuery<CamareraHk[]>({
    queryKey: [QK, 'hk-camareras'], queryFn: () => hotelRpc<CamareraHk[]>('hk_camareras_listar', {}),
    refetchInterval: 15000,
  });
  const [vista, setVista] = useState<Vista>('tablero');
  const [filtro, setFiltro] = useState<Filtro>('sucias');
  const [piso, setPiso] = useState<string>('');
  const [deCamarera, setDeCamarera] = useState<string>('');
  const [modoAsignar, setModoAsignar] = useState(false);
  const [selec, setSelec] = useState<string[]>([]);
  const [camareraSel, setCamareraSel] = useState('');
  const [planificar, setPlanificar] = useState(false);
  const [minibar, setMinibar] = useState<HkHabitacion | null>(null);
  const [objeto, setObjeto] = useState<HkHabitacion | 'libre' | null>(null);
  const [inspeccionar, setInspeccionar] = useState<HkHabitacion | null>(null);
  const [cierre, setCierre] = useState(false);
  const invalidar = () => void qc.invalidateQueries({ queryKey: [QK] });

  const activas = camareras.filter((c) => c.activo);
  const pisos = useMemo(() => Array.from(new Set(habs.map((h) => h.piso || ''))).sort(natural), [habs]);
  const visibles = useMemo(() => habs
    .filter((h) => (filtro === 'sucias' ? h.estado_limpieza === 'sucia'
      : filtro === 'sin_asignar' ? h.estado_limpieza === 'sucia' && !h.camarera_id
      : filtro === 'en_curso' ? h.tarea_estado === 'en_proceso' || h.tarea_estado === 'pausa' : true))
    .filter((h) => (piso ? (h.piso || '') === piso : true))
    .filter((h) => (deCamarera ? h.camarera_id === deCamarera : true))
    .sort((a, b) => natural(a.piso ?? '', b.piso ?? '') || natural(a.numero, b.numero)),
  [habs, filtro, piso, deCamarera]);
  const porPiso = useMemo(() => {
    const m = new Map<string, HkHabitacion[]>();
    visibles.forEach((h) => { const k = h.piso || ''; m.set(k, [...(m.get(k) ?? []), h]); });
    return [...m.entries()];
  }, [visibles]);
  const atrasadas = habs.filter(atrasada);
  const rapidas = habs.filter((h) => h.tarea_estado === 'terminada' && h.rapida && h.estado_limpieza !== 'inspeccionada');

  const marcar = useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: string }) => hotelRpc('habitacion_limpieza', { _hab: id, _estado: estado }),
    onSuccess: invalidar, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const dnd = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) => hotelRpc('habitacion_no_molestar', { _hab: id, _activo: activo }),
    onSuccess: invalidar, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const prioridad = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) => hotelRpc('hk_prioridad', { _hab: id, _activo: activo, _motivo: null }),
    onSuccess: (_r, v) => { toast.success(v.activo ? 'Prioridad pedida: sube al primer lugar de la camarera' : 'Prioridad quitada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const reportar = useMutation({
    mutationFn: ({ id, obs }: { id: string; obs: 'ocupada' | 'vacia' }) => hotelRpc<{ discrepancia: boolean }>('hk_reportar', { _hab: id, _observado: obs }),
    onSuccess: (r) => { toast[r.discrepancia ? 'warning' : 'success'](r.discrepancia ? 'No coincide con el sistema: se avisó a recepción' : 'Coincide con el sistema'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const [asignando, setAsignando] = useState(false);
  const asignar = async (camarera: string | null) => {
    setAsignando(true);
    const ok = await asignarConTope(selec, camarera, pedirJust);
    setAsignando(false);
    if (ok) { toast.success(camarera ? `${selec.length} habitación(es) asignadas` : 'Asignación quitada'); setSelec([]); setModoAsignar(false); invalidar(); }
  };

  const chip = (k: Filtro, l: string, cls: string) => (
    <button type="button" onClick={() => setFiltro(k)} className={`rounded-full px-4 py-2 text-sm font-medium ${filtro === k ? cls : 'bg-muted text-muted-foreground'}`}>{l}</button>
  );

  return (
    <div className="space-y-3 p-3 sm:p-6">
      {dlg.ui}
      {minibar && <ModalMinibar hab={minibar} onClose={() => setMinibar(null)} onDone={() => { setMinibar(null); invalidar(); }} />}
      {objeto && <ModalObjeto hab={objeto === 'libre' ? null : objeto} onClose={() => setObjeto(null)} onDone={() => { setObjeto(null); invalidar(); }} />}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="flex items-center gap-2 text-lg font-semibold"><SprayCan className="h-5 w-5" /> Housekeeping</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => openTab({ id: '/hotel/mi-dia', title: 'Mi día (camareras)', path: '/hotel/mi-dia' })}>
            <Smartphone className="mr-1 h-4 w-4" /> Mi día (camareras)
          </Button>
          <div className="flex flex-wrap gap-1 rounded-md border p-1 text-sm">
            {(['tablero', 'inspeccion', ...(esSupervisora ? ['profundas', 'insumos'] : []), 'camareras', 'llaves', ...(esSupervisora ? ['gestion'] : []), 'objetos', 'discrepancias', 'ajustes'] as Vista[]).map((v) => (
              <button key={v} type="button" onClick={() => setVista(v)} className={`rounded px-3 py-1 ${vista === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
                {({ tablero: 'Tablero', inspeccion: `Inspección (${habs.filter((h) => h.inspeccion).length})`, camareras: `Camareras (${activas.length})`,
                   profundas: 'Profundas', insumos: 'Insumos', llaves: 'Llaves', gestion: 'Gestión', objetos: 'Objetos perdidos', discrepancias: `Discrepancias (${habs.filter((h) => h.discrepancia).length})`, ajustes: 'Ajustes' } as Record<Vista, string>)[v]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {vista === 'objetos' && <Objetos onNuevo={() => setObjeto('libre')} />}
      {vista === 'discrepancias' && <Discrepancias />}
      {vista === 'camareras' && <Camareras camareras={camareras} puedeEditar={esSupervisora} />}
      {vista === 'llaves' && <Llaves camareras={camareras} puedeEditar={esSupervisora} />}
      {vista === 'ajustes' && <><AjustesHk puedeEditar={esSupervisora} /><AjustesF4 puedeEditar={esSupervisora} /></>}
      {vista === 'profundas' && esSupervisora && <Profundas camareras={camareras} />}
      {vista === 'insumos' && esSupervisora && <Insumos puedeEditar={esSupervisora} />}
      {vista === 'gestion' && esSupervisora && <GestionHk />}
      {inspeccionar && <PanelInspeccion hab={inspeccionar} onClose={() => setInspeccionar(null)} onDone={() => { setInspeccionar(null); invalidar(); }} />}
      {vista === 'inspeccion' && <Inspeccion habs={habs} onInspeccionar={(h) => (esSupervisora ? setInspeccionar(h) : toast.error('La inspección la hace la ama de llaves o un supervisor'))} />}

      {vista === 'tablero' && <>
        <AreasEstadoBanda />
        {/* Carga por camarera */}
        {activas.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {activas.map((c) => {
              const excede = Number(c.creditos_hoy) > c.tope;
              const sel = deCamarera === c.id;
              return (
                <button key={c.id} type="button" onClick={() => setDeCamarera(sel ? '' : c.id)}
                  title={`${c.nombre}: ${c.creditos_hoy} de ${c.tope} créditos · ${c.terminadas_hoy} de ${c.tareas_hoy} terminadas`}
                  className={`rounded-lg border px-3 py-1.5 text-left text-xs ${sel ? 'border-primary ring-2 ring-primary/30' : ''} ${excede ? 'border-red-400 bg-red-50 dark:bg-red-950/30' : 'bg-card'}`}>
                  <div className="font-semibold">{c.nombre}</div>
                  <div className="tabular-nums text-muted-foreground">
                    <span className={excede ? 'font-semibold text-red-700 dark:text-red-400' : ''}>{c.creditos_hoy}/{c.tope} cr</span> · {c.terminadas_hoy}/{c.tareas_hoy} ✓
                  </div>
                  <div className="mt-1 h-1 w-24 overflow-hidden rounded bg-muted">
                    <div className="h-full bg-emerald-500" style={{ width: `${c.tareas_hoy ? (c.terminadas_hoy / c.tareas_hoy) * 100 : 0}%` }} />
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            Aún no hay camareras. Créelas en la pestaña <button type="button" className="underline" onClick={() => setVista('camareras')}>Camareras</button> con su PIN para que trabajen desde «Mi día».
          </div>
        )}

        {(atrasadas.length > 0 || rapidas.length > 0) && (
          <div className="space-y-1">
            {atrasadas.map((h) => (
              <div key={h.id} className="flex items-center gap-2 rounded-md border border-orange-300 bg-orange-50 px-3 py-1.5 text-xs text-orange-900 dark:border-orange-800 dark:bg-orange-950/30 dark:text-orange-200">
                <Clock className="h-3.5 w-3.5" /> La {h.numero} lleva {h.minutos} min en limpieza ({h.camarera}); el estándar es {h.estandar} min.
              </div>
            ))}
            {rapidas.length > 0 && (
              <div className="flex items-center gap-2 rounded-md border border-sky-300 bg-sky-50 px-3 py-1.5 text-xs text-sky-900 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-200">
                <AlertTriangle className="h-3.5 w-3.5" /> Terminadas muy rápido, conviene inspeccionarlas: {rapidas.map((h) => `${h.numero} (${h.minutos} min)`).join(', ')}.
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {chip('sucias', `Sucias (${habs.filter((h) => h.estado_limpieza === 'sucia').length})`, 'bg-red-600 text-white')}
          {chip('sin_asignar', `Sin camarera (${habs.filter((h) => h.estado_limpieza === 'sucia' && !h.camarera_id).length})`, 'bg-amber-500 text-amber-950')}
          {chip('en_curso', `En limpieza (${habs.filter((h) => h.tarea_estado === 'en_proceso' || h.tarea_estado === 'pausa').length})`, 'bg-blue-600 text-white')}
          {chip('todas', `Todas (${habs.length})`, 'bg-primary text-primary-foreground')}
          {pisos.length > 1 && (
            <select className="rounded-full border bg-background px-3 py-2 text-sm" value={piso} onChange={(e) => setPiso(e.target.value)}>
              <option value="">Todos los pisos</option>
              {pisos.map((p) => <option key={p} value={p}>{etiquetaPiso(p)}</option>)}
            </select>
          )}
          {esSupervisora && <>
            <Button size="sm" variant={cierre ? 'default' : 'outline'} onClick={() => setCierre(!cierre)}>
              <ClipboardList className="mr-1 h-4 w-4" /> Cierre de turno
            </Button>
            <Button size="sm" variant={planificar ? 'default' : 'outline'} onClick={() => { setPlanificar(!planificar); setModoAsignar(false); }}>
              <Sparkles className="mr-1 h-4 w-4" /> Planificar el día
            </Button>
            <Button size="sm" variant={modoAsignar ? 'default' : 'outline'} onClick={() => { setModoAsignar(!modoAsignar); setSelec([]); setPlanificar(false); }}>
              <Users className="mr-1 h-4 w-4" /> {modoAsignar ? 'Cancelar asignación' : 'Asignar a mano'}
            </Button>
          </>}
          {isFetching && <span className="text-xs text-muted-foreground">Actualizando…</span>}
        </div>

        {cierre && <CierreTurno onClose={() => setCierre(false)} />}
        {planificar && <Planificador camareras={activas} onClose={() => setPlanificar(false)} onHecho={() => { setPlanificar(false); invalidar(); }} />}

        {modoAsignar && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-violet-300 bg-violet-50 p-2 text-sm dark:border-violet-800 dark:bg-violet-900/20">
            <span>{selec.length} seleccionada(s). Toque las tarjetas para elegirlas.</span>
            <select className={`${fieldCls} w-52`} value={camareraSel} onChange={(e) => setCamareraSel(e.target.value)}>
              <option value="">— Camarera —</option>
              {activas.map((c) => <option key={c.id} value={c.id}>{c.nombre} ({c.creditos_hoy}/{c.tope} cr)</option>)}
            </select>
            <Button size="sm" disabled={selec.length === 0 || !camareraSel} isLoading={asignando} onClick={() => void asignar(camareraSel)}>Asignar</Button>
            <Button size="sm" variant="outline" disabled={selec.length === 0} onClick={() => void asignar(null)}>Quitar camarera</Button>
          </div>
        )}

        {isLoading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : visibles.length === 0 ? (
          <div className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
            {filtro === 'sucias' ? 'No hay habitaciones sucias — todo al día ✓' : 'Sin habitaciones'}
          </div>
        ) : porPiso.map(([p, lista]) => (
          <section key={p} className="space-y-2">
            <h2 className="flex items-center gap-2 border-b pb-1 text-sm font-semibold">
              {etiquetaPiso(p)}
              <span className="font-normal text-muted-foreground">
                {lista.length} hab. · {lista.filter((h) => h.tarea_estado === 'terminada').length} terminadas · {lista.filter((h) => h.tarea_estado === 'en_proceso').length} en limpieza
              </span>
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {lista.map((h) => {
                const limp = LIMPIEZA[h.estado_limpieza] ?? LIMPIEZA.sucia;
                const enProceso = marcar.isPending && marcar.variables?.id === h.id;
                const elegida = selec.includes(h.id);
                const est = h.tarea_estado ? ESTADO_TAREA_HK[h.tarea_estado] : null;
                return (
                  <div key={h.id}
                    onClick={modoAsignar ? () => setSelec(elegida ? selec.filter((x) => x !== h.id) : [...selec, h.id]) : undefined}
                    className={`rounded-xl border-2 p-3 shadow-sm ${modoAsignar ? 'cursor-pointer' : ''} ${elegida ? 'border-violet-500 ring-2 ring-violet-300' : atrasada(h) ? 'border-orange-400' : h.estado_mant !== 'ok' ? 'border-rose-400' : 'border-border'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-1.5 text-2xl font-bold">
                          {h.numero}
                          {h.prioridad_motivo && <Zap className="h-5 w-5 text-amber-500" aria-label="Prioridad" />}
                        </div>
                        <div className="text-xs text-muted-foreground">{h.tipo}{h.tarea_tipo ? ` · ${({ salida: 'Salida', ocupada: 'Ocupada', libre: 'Libre', profunda: 'Profunda' } as Record<string, string>)[h.tarea_tipo]} · ${h.creditos} cr` : ''}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${limp.cls}`}>{limp.label}</span>
                        {est && <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${est.cls}`}>{est.label}</span>}
                      </div>
                    </div>

                    {(h.camarera || h.tarea_estado) && (
                      <div className="mt-1.5 flex items-center justify-between gap-2 text-xs">
                        <span className="rounded-full bg-violet-100 px-2 py-0.5 font-medium text-violet-800 dark:bg-violet-900/30 dark:text-violet-300">{h.camarera ?? 'Sin camarera'}</span>
                        {h.minutos != null && (
                          <span className={`tabular-nums ${atrasada(h) ? 'font-semibold text-orange-600' : 'text-muted-foreground'}`}>
                            <Clock className="mr-0.5 inline h-3 w-3" />{h.minutos} / {h.estandar} min
                            {h.tarea_estado === 'pausa' && h.pausa_motivo ? ` · ${MOTIVO_PAUSA[h.pausa_motivo] ?? h.pausa_motivo}` : ''}
                          </span>
                        )}
                      </div>
                    )}
                    {h.prioridad_motivo && <div className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-400">⚡ {h.prioridad_motivo}</div>}

                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${h.ocupada ? 'bg-slate-700 text-white dark:bg-slate-600' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
                        {h.ocupada ? <DoorClosed className="h-3 w-3" /> : <DoorOpen className="h-3 w-3" />} {h.ocupada ? 'Ocupada' : 'Libre'}
                      </span>
                      {h.sale_hoy && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"><LogOut className="h-3 w-3" /> Sale hoy</span>}
                      {h.llega_hoy && <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-800 dark:bg-sky-900/30 dark:text-sky-300"><LogIn className="h-3 w-3" /> Llega hoy{h.llegada_hora ? ` ${h.llegada_hora.slice(0, 5)}` : ''}</span>}
                      {h.vip && <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-[11px] font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"><Star className="h-3 w-3" /> VIP</span>}
                      {h.no_molestar && <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-100 px-2 py-0.5 text-[11px] font-medium text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-300"><BellOff className="h-3 w-3" /> No molestar</span>}
                      {h.discrepancia && <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300"><AlertTriangle className="h-3 w-3" /> Discrepancia</span>}
                      {h.estado_mant !== 'ok' && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-800 dark:bg-rose-900/30 dark:text-rose-300">{MANT[h.estado_mant] ?? h.estado_mant}</span>}
                      {h.rapida && h.tarea_estado === 'terminada' && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-800 dark:bg-sky-900/30 dark:text-sky-300">Revisar: muy rápida</span>}
                      {!!h.relimpieza && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">Re-limpieza{h.relimpieza > 1 ? ` ×${h.relimpieza}` : ''}</span>}
                      {!!h.faltantes && h.tarea_estado === 'terminada' && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">Faltó {h.faltantes} paso(s)</span>}
                      {h.inspeccion && <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${h.inspeccion === 'obligatoria' ? 'bg-amber-200 text-amber-900' : 'bg-muted text-muted-foreground'}`}>Por inspeccionar{h.inspeccion === 'obligatoria' ? '' : ' (muestra)'}</span>}
                    </div>

                    {h.huesped && <div className="mt-1 truncate text-xs text-muted-foreground">👤 {h.huesped}</div>}

                    {!modoAsignar && <>
                      <div className="mt-2 grid grid-cols-3 gap-1.5">
                        <Button size="sm" className="h-10 bg-red-600 text-white hover:bg-red-700" isLoading={enProceso}
                          disabled={h.estado_limpieza === 'sucia'} onClick={() => marcar.mutate({ id: h.id, estado: 'sucia' })}>Sucia</Button>
                        <Button size="sm" className="h-10 bg-sky-600 text-white hover:bg-sky-700" isLoading={enProceso}
                          disabled={h.estado_limpieza === 'limpia'} onClick={() => marcar.mutate({ id: h.id, estado: 'limpia' })}>Limpia</Button>
                        {h.tarea_estado === 'terminada' && h.estado_limpieza !== 'inspeccionada' && esSupervisora
                          ? <Button size="sm" className="h-10 bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => setInspeccionar(h)}><ClipboardCheck className="mr-1 h-4 w-4" />Inspec.</Button>
                          : <Button size="sm" className="h-10 bg-emerald-600 text-white hover:bg-emerald-700" isLoading={enProceso}
                              disabled={h.estado_limpieza === 'inspeccionada'} onClick={() => marcar.mutate({ id: h.id, estado: 'inspeccionada' })}>Inspecc.</Button>}
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1 text-xs">
                        {h.estado_limpieza === 'sucia' && (
                          <button type="button" className={`rounded border px-2 py-1 hover:bg-muted ${h.prioridad_manual ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/30' : ''}`}
                            onClick={() => prioridad.mutate({ id: h.id, activo: !h.prioridad_manual })}>
                            <Zap className="mr-1 inline h-3 w-3" />{h.prioridad_manual ? 'Quitar prioridad' : 'Prioridad'}
                          </button>
                        )}
                        <button type="button" className="rounded border px-2 py-1 hover:bg-muted" onClick={() => dnd.mutate({ id: h.id, activo: !h.no_molestar })}>
                          <BellOff className="mr-1 inline h-3 w-3" />{h.no_molestar ? 'Quitar no molestar' : 'No molestar'}
                        </button>
                        {h.ocupada && <button type="button" className="rounded border px-2 py-1 hover:bg-muted" onClick={() => setMinibar(h)}><Wine className="mr-1 inline h-3 w-3" />Minibar</button>}
                        <button type="button" className="rounded border px-2 py-1 hover:bg-muted" onClick={() => setObjeto(h)}><Package className="mr-1 inline h-3 w-3" />Objeto</button>
                        <button type="button" className="rounded border px-2 py-1 hover:bg-muted" onClick={() => reportar.mutate({ id: h.id, obs: h.ocupada ? 'vacia' : 'ocupada' })}>
                          La veo {h.ocupada ? 'vacía' : 'ocupada'}
                        </button>
                      </div>
                    </>}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </>}

    </div>
  );
}

// ── Planificación del día: reparto propuesto por créditos y pisos ──────────
function Planificador({ camareras, onClose, onHecho }: { camareras: CamareraHk[]; onClose: () => void; onHecho: () => void }) {
  const dlg = useDialogoEnLinea();
  const pedirJust: PedirJustificacion = async (detalle) =>
    (await dlg.pedir('Supera el tope de créditos', [{ key: 'j', label: 'Justificación (queda registrada)', minimo: 5, multilinea: true }], { detalle, aceptar: 'Asignar igual' }))?.j.trim() ?? null;
  const [elegidas, setElegidas] = useState<string[]>(() => camareras.filter((c) => c.tiene_pin).map((c) => c.id));
  const [propuesta, setPropuesta] = useState<Propuesta[] | null>(null);
  const [aplicando, setAplicando] = useState(false);
  const proponer = useMutation({
    mutationFn: () => hotelRpc<Propuesta[]>('hk_proponer_reparto', { _camareras: elegidas }),
    onSuccess: setPropuesta, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const aplicar = async () => {
    if (!propuesta) return;
    let just: string | undefined;
    if (propuesta.some((p) => p.excede && p.habitaciones.length)) {
      const j = await pedirJust('Algunas camareras superan su tope de créditos (riesgo ergonómico). Mejor sume otra camarera; si igual debe aplicarse, escriba la justificación.');
      if (!j) return;
      just = j;
    }
    setAplicando(true);
    let ok = true;
    for (const p of propuesta) {
      if (!p.habitaciones.length) continue;
      ok = (await asignarConTope(p.habitaciones.map((h) => h.id), p.camarera_id, pedirJust, p.excede ? just : undefined)) && ok;
    }
    setAplicando(false);
    if (ok) { toast.success('Reparto aplicado: cada camarera ya ve sus habitaciones en «Mi día»'); onHecho(); }
  };
  return (
    <Panel title="Planificar el día" onClose={onClose}>
      {dlg.ui}
      <div className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Elija quiénes trabajan hoy. El sistema reparte las habitaciones sucias sin camarera por pisos contiguos y créditos parejos
          (salida 3 · libre sucia 2 · ocupada 1), respetando el tope de cada una. Revise y ajuste después con «Asignar a mano».
        </p>
        <div className="flex flex-wrap gap-2">
          {camareras.map((c) => (
            <label key={c.id} className={`flex items-center gap-1.5 rounded border px-2 py-1 ${elegidas.includes(c.id) ? 'border-primary bg-primary/5' : ''}`}>
              <input type="checkbox" checked={elegidas.includes(c.id)}
                onChange={(e) => { setPropuesta(null); setElegidas(e.target.checked ? [...elegidas, c.id] : elegidas.filter((x) => x !== c.id)); }} />
              {c.nombre} <span className="text-xs text-muted-foreground">({c.creditos_hoy}/{c.tope})</span>
            </label>
          ))}
        </div>
        <Button size="sm" disabled={!elegidas.length} isLoading={proponer.isPending} onClick={() => proponer.mutate()}>Proponer reparto</Button>
        {propuesta && (
          <div className="space-y-2">
            {propuesta.map((p) => (
              <div key={p.camarera_id} className={`rounded-md border p-2 ${p.excede ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30' : ''}`}>
                <div className="flex items-center justify-between font-medium">
                  <span>{p.nombre}</span>
                  <span className={`tabular-nums text-xs ${p.excede ? 'font-semibold text-red-700 dark:text-red-400' : 'text-muted-foreground'}`}>
                    {p.carga} / {p.tope} créditos{p.excede ? ' · supera el tope' : ''}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {p.habitaciones.length === 0 && <span className="text-xs text-muted-foreground">Sin habitaciones nuevas</span>}
                  {p.habitaciones.map((h) => <span key={h.id} className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{h.numero}</span>)}
                </div>
              </div>
            ))}
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button size="sm" isLoading={aplicando} disabled={!propuesta.some((p) => p.habitaciones.length)} onClick={() => void aplicar()}>Aplicar reparto</Button>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

// ── Catálogo de camareras ──────────────────────────────────────────────────
function Camareras({ camareras, puedeEditar }: { camareras: CamareraHk[]; puedeEditar: boolean }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Partial<CamareraHk> & { pin?: string } | null>(null);
  const { data: empleados = [] } = useQuery<{ id: string; nombre: string; identificacion: string }[]>({
    queryKey: [QK, 'hk-empleados'], queryFn: () => hotelRpc('hk_empleados', {}), enabled: !!edit,
  });
  const guardar = useMutation({
    mutationFn: () => hotelRpc('hk_camarera_guardar', { _d: {
      id: edit?.id ?? null, nombre: edit?.nombre ?? '', empleado_id: edit?.empleado_id || null,
      pin: edit?.pin || null, creditos_max: edit?.creditos_max ?? null, activo: edit?.activo ?? true,
    } }),
    onSuccess: () => { toast.success('Camarera guardada'); setEdit(null); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-3xl text-xs text-muted-foreground">
          Cada camarera entra a «Mi día» con su PIN. Vincúlela con su ficha de Nómina para que pueda consultar sus registros en el Portal.
          Los tiempos de limpieza son un dato operativo: no reemplazan el control de jornada de Nómina ni sirven para descontar sueldo.
        </p>
        {puedeEditar && <Button size="sm" onClick={() => setEdit({ activo: true })}><Users className="mr-1 h-4 w-4" /> Nueva camarera</Button>}
      </div>

      {edit && (
        <Panel title={edit.id ? `Editar · ${edit.nombre}` : 'Nueva camarera'} onClose={() => setEdit(null)}>
          <div className="grid gap-3 text-sm sm:grid-cols-2">
            <Campo label="Nombre *"><input className={fieldCls} value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} /></Campo>
            <Campo label="Empleado de Nómina">
              <select className={fieldCls} value={edit.empleado_id ?? ''} onChange={(e) => setEdit({ ...edit, empleado_id: e.target.value || null })}>
                <option value="">— Sin vincular —</option>
                {empleados.map((x) => <option key={x.id} value={x.id}>{x.nombre} · {x.identificacion}</option>)}
              </select>
            </Campo>
            <Campo label={edit.id ? 'PIN nuevo (4 a 6 dígitos; vacío = no cambiar)' : 'PIN (4 a 6 dígitos)'}>
              <input className={fieldCls} inputMode="numeric" autoComplete="off" maxLength={6} value={edit.pin ?? ''}
                onChange={(e) => setEdit({ ...edit, pin: e.target.value.replace(/\D/g, '') })} />
            </Campo>
            <Campo label="Tope de créditos por turno (vacío = el del hotel)">
              <input className={fieldCls} type="number" min={1} max={60} value={edit.creditos_max ?? ''}
                onChange={(e) => setEdit({ ...edit, creditos_max: e.target.value ? Number(e.target.value) : null })} />
            </Campo>
            <label className="flex items-center gap-2"><input type="checkbox" checked={edit.activo ?? true} onChange={(e) => setEdit({ ...edit, activo: e.target.checked })} /> Activa</label>
            <div className="flex justify-end gap-2 sm:col-span-2">
              <Button variant="outline" size="sm" onClick={() => setEdit(null)}>Cancelar</Button>
              <Button size="sm" isLoading={guardar.isPending} disabled={!edit.nombre?.trim() || (!!edit.pin && edit.pin.length < 4)} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Panel>
      )}

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Nombre</th><th className="py-2 pr-2">Empleado (Nómina)</th><th className="py-2 pr-2">PIN</th>
            <th className="py-2 pr-2 text-right">Tope</th><th className="py-2 pr-2 text-right">Hoy</th><th className="py-2 pr-2">Aviso de privacidad</th><th className="py-2 pr-2">Estado</th><th className="py-2 pr-2"></th>
          </tr></thead>
          <tbody>
            {camareras.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">Sin camareras registradas.</td></tr>}
            {camareras.map((c, i) => (
              <tr key={c.id} className={`border-b last:border-0 ${c.activo ? '' : 'text-muted-foreground'}`}>
                <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                <td className="py-1.5 pr-2 font-medium">{c.nombre}</td>
                <td className="py-1.5 pr-2">{c.empleado ?? <span className="text-muted-foreground">—</span>}</td>
                <td className="py-1.5 pr-2">{c.tiene_pin ? (c.bloqueada ? <span className="text-red-600">Bloqueado</span> : 'Sí') : <span className="text-amber-700">Falta</span>}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{c.tope}{c.creditos_max == null ? '*' : ''}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{c.creditos_hoy} cr · {c.terminadas_hoy}/{c.tareas_hoy}</td>
                <td className="py-1.5 pr-2 text-xs">{c.aviso_aceptado_at ? `Leído ${fmtFecha(c.aviso_aceptado_at)}` : <span className="text-muted-foreground">Pendiente</span>}</td>
                <td className="py-1.5 pr-2">{c.activo ? 'Activa' : 'Inactiva'}</td>
                <td className="py-1.5 pr-2 text-right">{puedeEditar && <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setEdit({ ...c })}>Editar</Button>}</td>
              </tr>
            ))}
          </tbody>
          {camareras.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
            <td colSpan={5} className="py-2 pl-3">Total: {camareras.length} ({camareras.filter((c) => c.activo).length} activas)</td>
            <td className="py-2 pr-2 text-right tabular-nums">{camareras.reduce((a, c) => a + Number(c.creditos_hoy), 0)} cr</td>
            <td colSpan={3} className="py-2 pr-2 text-xs font-normal text-muted-foreground">* tope general del hotel</td>
          </tr></tfoot>}
        </table>
      </div>
    </div>
  );
}

export function ModalMinibar({ hab, onClose, onDone }: { hab: HkHabitacion; onClose: () => void; onDone: () => void }) {
  const [busca, setBusca] = useState('');
  const [items, setItems] = useState<Record<string, { p: ProdMinibar; n: number }>>({});
  const { data: prods = [] } = useQuery<ProdMinibar[]>({ queryKey: [QK, 'minibar-prod', busca], queryFn: () => hotelRpc<ProdMinibar[]>('minibar_productos', { _busca: busca || null }) });
  const cargar = useMutation({
    mutationFn: () => hotelRpc<{ total: number }>('minibar_cargar', { _hab: hab.id, _items: Object.values(items).filter((x) => x.n > 0).map((x) => ({ producto_id: x.p.id, cantidad: x.n })) }),
    onSuccess: (r) => { toast.success(`Minibar cargado a la habitación: ${money(Number(r.total))}`); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const total = Object.values(items).reduce((a, x) => a + x.n * Number(x.p.precio), 0);
  return (
    <Modal title={`Minibar · habitación ${hab.numero}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <input className={fieldCls} placeholder="Buscar producto…" value={busca} onChange={(e) => setBusca(e.target.value)} />
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {prods.map((p) => {
            const n = items[p.id]?.n ?? 0;
            return (
              <div key={p.id} className="flex items-center justify-between gap-2 rounded border px-2 py-1">
                <span>{p.nombre} <span className="text-xs text-muted-foreground">{money(Number(p.precio))}</span></span>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" className="h-8 w-8 p-0" disabled={n === 0} onClick={() => setItems({ ...items, [p.id]: { p, n: n - 1 } })}>−</Button>
                  <span className="w-6 text-center tabular-nums">{n}</span>
                  <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setItems({ ...items, [p.id]: { p, n: n + 1 } })}>+</Button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between border-t pt-3">
          <span className="font-semibold">Total: {money(total)}</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button isLoading={cargar.isPending} disabled={total <= 0} onClick={() => cargar.mutate()}>Cargar a la habitación</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function ModalObjeto({ hab, onClose, onDone }: { hab: HkHabitacion | null; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ descripcion: '', lugar: '', encontrado_por: '' });
  const guardar = useMutation({
    mutationFn: () => hotelRpc<{ codigo: string }>('objeto_registrar', { _d: { habitacion_id: hab?.id ?? null, ...f } }),
    onSuccess: (r) => { toast.success(`Objeto registrado (${r.codigo}): rotúlelo con ese código`); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Modal title={hab ? `Objeto encontrado · habitación ${hab.numero}` : 'Objeto encontrado'} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <Campo label="Descripción *"><input className={fieldCls} value={f.descripcion} placeholder="Ej.: gafas de sol negras, cargador de celular" onChange={(e) => setF({ ...f, descripcion: e.target.value })} /></Campo>
        {!hab && <Campo label="Lugar"><input className={fieldCls} value={f.lugar} placeholder="Ej.: piscina, restaurante Darwin" onChange={(e) => setF({ ...f, lugar: e.target.value })} /></Campo>}
        <Campo label="Encontrado por"><input className={fieldCls} value={f.encontrado_por} onChange={(e) => setF({ ...f, encontrado_por: e.target.value })} /></Campo>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!f.descripcion.trim()} onClick={() => guardar.mutate()}>Registrar</Button>
        </div>
      </div>
    </Modal>
  );
}

function Objetos({ onNuevo }: { onNuevo: () => void }) {
  const dlg = useDialogoEnLinea();
  const qc = useQueryClient();
  const [estado, setEstado] = useState('guardado');
  const { data = [] } = useQuery<Objeto[]>({ queryKey: [QK, 'objetos', estado], queryFn: () => hotelRpc<Objeto[]>('objetos_listar', { _estado: estado || null }) });
  const cerrar = useMutation({
    mutationFn: (x: { id: string; estado: string; a?: string; doc?: string }) => hotelRpc('objeto_cerrar', { _id: x.id, _estado: x.estado, _entregado_a: x.a ?? null, _doc: x.doc ?? null }),
    onSuccess: () => { toast.success('Objeto actualizado'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <div className="space-y-2">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <select className={`${fieldCls} w-48`} value={estado} onChange={(e) => setEstado(e.target.value)}>
          <option value="guardado">En custodia</option><option value="entregado">Entregados</option><option value="descartado">Descartados</option><option value="">Todos</option>
        </select>
        <Button size="sm" onClick={onNuevo}><Package className="mr-1 h-4 w-4" /> Registrar objeto</Button>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Código</th><th className="py-2 pr-2">Fecha</th><th className="py-2 pr-2">Dónde</th>
            <th className="py-2 pr-2">Objeto</th><th className="py-2 pr-2">Huésped probable</th><th className="py-2 pr-2">Estado</th><th className="py-2 pr-2"></th>
          </tr></thead>
          <tbody>
            {data.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Sin objetos.</td></tr>}
            {data.map((o, i) => (
              <tr key={o.id} className="border-b last:border-0">
                <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                <td className="py-1.5 pr-2 font-mono">{o.codigo}</td>
                <td className="py-1.5 pr-2">{fmtFecha(o.fecha)}</td>
                <td className="py-1.5 pr-2">{o.habitacion ? `Hab. ${o.habitacion}` : o.lugar ?? '—'}</td>
                <td className="py-1.5 pr-2">{o.descripcion}{o.encontrado_por && <div className="text-xs text-muted-foreground">por {o.encontrado_por}</div>}</td>
                <td className="py-1.5 pr-2">{o.huesped ?? '—'}{o.reserva && <span className="ml-1 text-xs text-muted-foreground">{o.reserva}</span>}</td>
                <td className="py-1.5 pr-2 capitalize">{o.estado}</td>
                <td className="py-1.5 pr-2 whitespace-nowrap">
                  {o.estado === 'guardado' && <>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={async () => {
                      const r = await dlg.pedir(`Entregar ${o.codigo} · ${o.descripcion}`, [
                        { key: 'a', label: 'Nombre completo de quien recibe', minimo: 3 }, { key: 'doc', label: 'Documento de identidad', minimo: 5 }], { aceptar: 'Registrar entrega' });
                      if (r) cerrar.mutate({ id: o.id, estado: 'entregado', a: r.a.trim(), doc: r.doc.trim() });
                    }}>Entregar</Button>
                    <Button size="sm" variant="outline" className="ml-1 h-7 px-2 text-xs text-red-600" onClick={async () => { if (await dlg.confirmar(`¿Descartar o donar ${o.codigo}?`, { detalle: o.descripcion, aceptar: 'Descartar', peligro: true })) cerrar.mutate({ id: o.id, estado: 'descartado' }); }}>Descartar</Button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
          {data.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={8} className="py-2 pl-3">Total: {data.length}</td></tr></tfoot>}
        </table>
      </div>
    </div>
  );
}

function Discrepancias() {
  const dlg = useDialogoEnLinea();
  const qc = useQueryClient();
  const { data = [] } = useQuery<Discrepancia[]>({ queryKey: [QK, 'discrepancias'], queryFn: () => hotelRpc<Discrepancia[]>('hk_discrepancias_listar', {}) });
  const resolver = useMutation({
    mutationFn: (x: { id: string; r: string }) => hotelRpc('hk_discrepancia_resolver', { _id: x.id, _resolucion: x.r }),
    onSuccess: () => { toast.success('Discrepancia resuelta'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <div className="space-y-2">
      {dlg.ui}
      <p className="text-sm text-muted-foreground">
        Lo que vio la camarera no coincide con el sistema: una habitación «vacía» con huésped en casa puede ser un check-out no registrado o una salida sin pagar;
        una «ocupada» libre, un huésped sin registrar.
      </p>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Fecha</th><th className="py-2 pr-2">Hab.</th><th className="py-2 pr-2">Camarera vio</th>
            <th className="py-2 pr-2">Sistema dice</th><th className="py-2 pr-2">Reserva</th><th className="py-2 pr-2">Nota</th><th className="py-2 pr-2"></th>
          </tr></thead>
          <tbody>
            {data.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Sin discrepancias pendientes ✓</td></tr>}
            {data.map((d, i) => (
              <tr key={d.id} className="border-b last:border-0">
                <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                <td className="py-1.5 pr-2">{fmtFecha(d.fecha)}</td>
                <td className="py-1.5 pr-2 font-mono font-semibold">{d.habitacion}</td>
                <td className="py-1.5 pr-2">{d.observado === 'vacia' ? 'Vacía' : 'Ocupada'}</td>
                <td className="py-1.5 pr-2">{d.sistema === 'vacia' ? 'Libre' : 'Ocupada'}</td>
                <td className="py-1.5 pr-2 font-mono">{d.reserva ?? '—'}</td>
                <td className="py-1.5 pr-2 text-muted-foreground">{d.nota ?? ''}</td>
                <td className="py-1.5 pr-2">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={async () => { const v = await dlg.pedir(`Resolver discrepancia · ${d.habitacion}`, [{ key: 'r', label: '¿Cómo se resolvió?', minimo: 3, multilinea: true }], { aceptar: 'Resolver' }); if (v) resolver.mutate({ id: d.id, r: v.r.trim() }); }}>Resolver</Button>
                </td>
              </tr>
            ))}
          </tbody>
          {data.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={8} className="py-2 pl-3">Total: {data.length}</td></tr></tfoot>}
        </table>
      </div>
    </div>
  );
}
